/* Shared helpers for the TradingView Lightweight Charts™ surface on the Portfolio
   page. Canvas can't read CSS custom properties, so the active theme's leaf tokens
   are resolved once; the app is dark-only, so there's no theme-switch handling.
   Ported from the nq-design-lab candles POC. */
import * as React from 'react';
import type { Time } from 'lightweight-charts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Lightweight Charts hands back a UTCTimestamp (number, seconds), a 'YYYY-MM-DD'
   string, or a BusinessDay object depending on the source; normalize all to a
   "Mon DD" label. */
export const fmtTvDate = (t: Time): string => {
  if (typeof t === 'number') {
    const d = new Date(t * 1000);
    return `${MONTHS[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}`;
  }
  if (typeof t === 'string') {
    const [, m, d] = t.split('-').map(Number);
    return `${MONTHS[(m || 1) - 1]} ${String(d).padStart(2, '0')}`;
  }
  if (typeof t === 'object' && 'year' in t) {
    return `${MONTHS[(t.month || 1) - 1]} ${String(t.day).padStart(2, '0')}`;
  }
  return String(t);
};

export const cssVar = (name: string, fallback: string): string => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

/* $ ↔ % toggle: express a candle / line series as percent return. */
export type TvCandle = { time: Time; open: number; high: number; low: number; close: number };
export type TvLine = { time: Time; value: number };
const asPct = (v: number, base: number) => (v / base - 1) * 100;

/* Period-over-period return: rebase each candle to the PRIOR candle's close, so a
   bar's % is that period's own move (the last bar matches "Today's results"),
   not a running total from the range start. The first bar has no prior close, so
   it rebases to its own open (its open sits at 0%). A non-positive baseline
   yields a flat 0% bar. */
export const toPeriodReturnCandles = (candles: readonly TvCandle[]): TvCandle[] =>
  candles.map((c, i) => {
    const baseline = i === 0 ? c.open : candles[i - 1].close;
    return baseline > 0
      ? {
          time: c.time,
          open: asPct(c.open, baseline),
          high: asPct(c.high, baseline),
          low: asPct(c.low, baseline),
          close: asPct(c.close, baseline),
        }
      : { time: c.time, open: 0, high: 0, low: 0, close: 0 };
  });

/* Period-over-period return for the benchmark line: each point relative to the
   prior point's value; the first point is its own baseline (0%). */
export const toPeriodReturnLine = (line: readonly TvLine[]): TvLine[] =>
  line.map((p, i) => {
    const baseline = i === 0 ? p.value : line[i - 1].value;
    return { time: p.time, value: baseline > 0 ? asPct(p.value, baseline) : 0 };
  });

/* lightweight-charts price-scale formats for each mode. */
export const DOLLAR_PRICE_FORMAT = { type: 'price' as const, precision: 0, minMove: 1 };
export const PCT_PRICE_FORMAT = {
  type: 'custom' as const,
  minMove: 0.01,
  formatter: (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(1)}%`,
};

/* Series colours resolved from the active (dark) theme's leaf tokens, with
   hex fallbacks. These live in JS (not JSX `style`), so the no-hex ESLint rule
   does not apply — canvas colours can't come from CSS custom properties. */
export const tvThemeColors = () => ({
  up: cssVar('--nq-up', '#2ee66b'),
  down: cssVar('--nq-down', '#ff4d5e'),
  bench: cssVar('--nq-gold', '#f7941d'),
  primary: cssVar('--nq-blue', '#25b4e0'),
});

/* Static dark chart chrome. JS config object, ESLint-exempt (see above). */
export const TV_CHROME = {
  textColor: 'rgba(255,255,255,0.5)',
  vertGrid: 'rgba(255,255,255,0.04)',
  horzGrid: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  fontFamily: 'inherit',
};

/* Floating card matching the Performance tooltip: muted header + content rows. */
export function TvTooltipCard({
  style,
  children,
}: {
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div
      style={style}
      className="pointer-events-none absolute z-20 min-w-[200px] w-max max-w-[340px] rounded-xl border border-border bg-[color:var(--nq-bg-deep)]/98 px-3.5 py-2.5 text-[13px] shadow-[var(--shadow-popover)]"
    >
      {children}
    </div>
  );
}

/* One row of the tooltip: label · value · pct (3-col grid like the compare card). */
export function TvRow({
  label,
  value,
  pct,
  pctClass,
  strong,
  bordered,
}: {
  label: string;
  value?: string;
  pct?: string;
  pctClass?: string;
  strong?: boolean;
  bordered?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_auto_72px] items-center gap-3 whitespace-nowrap py-0.5 ${
        strong ? 'font-semibold' : ''
      } ${bordered ? 'mt-1.5 border-t border-[color:rgba(255,255,255,0.12)] pt-1.5' : ''}`}
    >
      <span className="min-w-0 truncate text-foreground">{label}</span>
      <span className="text-right tabular-nums text-foreground">{value ?? ''}</span>
      <span className={`text-right tabular-nums ${pctClass ?? 'text-muted-foreground'}`}>{pct ?? '—'}</span>
    </div>
  );
}
